/**
 * Elm-style optional value for app code (Vue, Pinia, composables, lib).
 *
 * Prefer Maybe over null/undefined in first-party types. Convert at edges:
 * - Wire / serde / JSON / DOM: `toMaybe` (nullish → Maybe), `fromMaybe` (Maybe → null)
 * - Index access: `atIndexMaybe`
 *
 * Do not assign `undefined` or type first-party APIs as `T | undefined`.
 * See docs/fp-conventions.md and #241.
 */

import { assertNever } from "./assertNever";

export type Just<T> = { readonly tag: "just"; readonly value: T };
export type Nothing = { readonly tag: "nothing" };
export type Maybe<T> = Just<T> | Nothing;

export const nothing: Nothing = { tag: "nothing" };

export function just<T>(value: T): Just<T> {
  return { tag: "just", value };
}

export function isJust<T>(m: Maybe<T>): m is Just<T> {
  return m.tag === "just";
}

export function isNothing<T>(m: Maybe<T>): m is Nothing {
  return m.tag === "nothing";
}

/**
 * Into Maybe: wire/DOM/index nullish → Just | Nothing.
 * Use at every boundary where Rust/JSON/DOM still uses null or undefined.
 * App code after this point should only see Maybe.
 */
export function toMaybe<T>(value: T | null | undefined): Maybe<T> {
  if (value === null || value === undefined) return nothing;
  return just(value);
}

/**
 * Out of Maybe: Just → value, Nothing → null.
 * Only when calling back into wire / Rust / APIs that expect null.
 * Do not use this to peel Maybe into app/local state. Stay on Maybe and
 * branch with `matchMaybe` / `isJust` / `isNothing`.
 */
export function fromMaybe<T>(m: Maybe<T>): T | null {
  return m.tag === "just" ? m.value : null;
}

/** Elm-style case split. Prefer this over `fromMaybe` + null checks in app code. */
export function matchMaybe<T, U>(
  m: Maybe<T>,
  arms: {
    readonly just: (value: T) => U;
    readonly nothing: () => U;
  },
): U {
  switch (m.tag) {
    case "just":
      return arms.just(m.value);
    case "nothing":
      return arms.nothing();
    default:
      return assertNever(m);
  }
}

/**
 * DOM / Vue native-attribute choke point only.
 * Vue's HTML typings use `string | undefined` to omit attrs; Nothing maps to
 * undefined here so the attribute is removed. Do not use for app state.
 */
export function toDomAttr(m: Maybe<string>): string | undefined {
  return m.tag === "just" ? m.value : undefined;
}

/** Index access without leaking `undefined` into callers. */
export function atIndexMaybe<T>(
  items: ReadonlyArray<T>,
  index: number,
): Maybe<T> {
  return toMaybe(items[index]);
}

export function mapMaybe<T, U>(m: Maybe<T>, fn: (value: T) => U): Maybe<U> {
  return m.tag === "just" ? just(fn(m.value)) : nothing;
}

export function andThenMaybe<T, U>(
  m: Maybe<T>,
  fn: (value: T) => Maybe<U>,
): Maybe<U> {
  return m.tag === "just" ? fn(m.value) : nothing;
}

/** Prefer this name over `unwrapOr` so it does not clash with `result.unwrapOr`. */
export function unwrapMaybeOr<T>(m: Maybe<T>, fallback: T): T {
  return m.tag === "just" ? m.value : fallback;
}
